import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import utils from 'utils';
import { config } from 'utils/config';
import { i18n } from 'utils/i18n';
import { analyticsClearErrors, analyticsInteraction } from 'utils/analytics';
import Storage from 'utils/storageManager';
import ANALYTICS from 'constants/analytics';
import THEMES from 'constants/themes';
import WINDOW_OBJECT_KEYS from 'constants/windowObjectKeys';
import GMI_SERVICE_PATHS from 'constants/gmiServicePaths';
import ERRORS from 'constants/errors';
import { login } from 'actions/authentication';
import { SESSION_STORAGE } from 'constants/session';
import useActions from 'hooks/useActions';
import { isFetchingEndpointSelector } from 'selectors/fetchingSelectors';
import { shouldAssociateProfileSelector } from 'selectors/authenticationSelectors';
import { gmaErrorMessagesSelector } from 'selectors/errorSelectors';
import Form from 'components/Form/Form';
import TextField from 'components/Form/TextField';
import PasswordField from 'components/Form/PasswordField';
import Checkbox from 'components/Form/Checkbox';
import Button from 'components/Button';
import Anchor from 'components/Anchor';
import GenericError from 'components/GenericError';

const { SIGN_IN_UNSUCCESSFUL, NEED_HELP } = ERRORS;

// Displaying service error message with a link for recovering forgot member number or password.
const getErrorRecoveryMessage = (isShowMemberNumber) =>
  i18n(
    'sign_in_unsuccessful_message_copy_2',
    [
      <Anchor
        className='signin__link--text link'
        key={0}
        href={config.getRedirectUrl(WINDOW_OBJECT_KEYS.FORGOT_PASSWORD_PAGE)}
        link
      >
        {i18n('signin_error_forgot_password')}
      </Anchor>,

      <Anchor
        className='signin__link--text link'
        key={1}
        href={config.getRedirectUrl(
          isShowMemberNumber ? WINDOW_OBJECT_KEYS.FORGOT_MEMBERNUMBER_PAGE : WINDOW_OBJECT_KEYS.FORGOT_USERNAME_PAGE
        )}
        link
      >
        {i18n('signin_error_forgot_member_number')}
      </Anchor>,
    ],
    { jsx: true }
  );

/**
 * SignIn Description
 *
 * standalone authentication form component for a user to enter their information
 * username/email
 * password
 * stay signed-in
 *
 * @param {object} props - React Props
 * @param {function} props.onSuccess - Function called after a successful login.
 *  - If ommited, the default action is to redirect the user to the Auth Home Page (Members page)
 * @param {function} props.onCancel - If provided, will display a "Cancel" button with this function as its' callback.
 *  i.e. used on the SignInModal to show a button set instead of only a single "Sign In" button
 *
 * @return {JSX}
 */

const SignIn = ({ onSuccess, onCancel, unAuthenticatedFlyout = false, stashAnalytics = true }) => {
  const { redirectBack } = utils.url.queryObject();
  const [submitted, setSubmitted] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const loginAction = useActions(login);
  const isFetching = useSelector((state) =>
    isFetchingEndpointSelector(state, { endpoint: 'profile/login', exactMatch: true })
  );

  const hasProfileErrors = useSelector((state) =>
    gmaErrorMessagesSelector(state, { statePath: GMI_SERVICE_PATHS.PROFILE_LOGIN })
  );

  // Passed to the loginAction in order to associate a profile to a reservation
  const associate_profile = useSelector(shouldAssociateProfileSelector);

  const isShowMemberNumber = config.getEnableMemberNumberText();

  const getLabelText = () => i18n(isShowMemberNumber ? 'signin_email_or_member_number' : 'signin_email_or_username');
  const getForgotLinkText = () =>
    i18n(isShowMemberNumber ? 'signin_forgot_member_number_text' : 'signin_forgot_username_text');

  useEffect(() => {
    submitted && !isFetching && onSuccess?.();
  }, [submitted, isFetching]);

  const onSubmit = (values) => {
    const { username, password, remember_credentials } = values;
    analyticsClearErrors();
    if (unAuthenticatedFlyout) {
      utils.analytics.interaction(
        'menu',
        'nav',
        i18n('signin_flyout_toggle').toLowerCase(),
        i18n('signin_flyout_toggle').toLowerCase()
      );
    } else {
      utils.analytics.interaction(ANALYTICS.MODAL, ANALYTICS.SIGN_IN_MODAL, ANALYTICS.SIGN_IN);
    }

    /* Checks for specific windowObjectKeys  for navigating to specific after login.
    Else it will by default redirect to home page/ go back to previous page. */
    const goToSpecificPagePath = localStorage.getItem(config.goToSpecificPagePath);

    // make the login call and then redirect to the authenticated homepage
    return loginAction({
      username,
      password,
      remember_credentials,
      associate_profile,
    }).then((res) => {
      // if the response contains no error messages, redirect
      // error messages will already be printed inside the component at ServiceErrors
      if (!res?.messages) {
        // Analytics
        analyticsInteraction(
          ANALYTICS.LOGIN,
          ANALYTICS.LOGIN_SUCCESSFUL,
          remember_credentials ? ANALYTICS.LOGIN_REMEMBER_ME : ANALYTICS.LOGIN_ORIGINAL,
          null,
          stashAnalytics // this sets the interaction to be stashed.
        );
        Storage.SessionStorage.set(SESSION_STORAGE.SHOW_EMAIL_UPDATE_MODAL, true);
        if (onSuccess) {
          setSubmitted(true);
        } else if (goToSpecificPagePath) {
          // Currently checking only viewModifyCancel details / Confirmation
          window.location.assign(config.getRedirectUrl(goToSpecificPagePath));
        } else if (redirectBack === 'true') {
          window.history.back();
        } else {
          window.location.assign(config.getRedirectUrl(WINDOW_OBJECT_KEYS.AUTH_HOME_PAGE));
        }
      } else {
        setErrorCount((prevState) => prevState + 1);
      }
    });
  };

  // showing different error message treatment based on the number of attempts made on unsuccessful login clicks
  const messageDesc =
    errorCount > 2 ? getErrorRecoveryMessage(isShowMemberNumber) : utils.i18n('sign_in_unsuccessful_message_copy_1');
  const showUnSuccessfulMessage = !(errorCount > 2);

  return (
    <Form onSubmit={onSubmit}>
      {({ handleSubmit }) => (
        <div className='signin'>
          {!!hasProfileErrors?.length && (
            <GenericError
              errors={[{ priority: showUnSuccessfulMessage ? SIGN_IN_UNSUCCESSFUL : NEED_HELP }]}
              setHtmlContent={showUnSuccessfulMessage}
              description={messageDesc}
            />
          )}
          {/* <ServiceErrors statePath={GMI_SERVICE_PATHS.PROFILE_LOGIN} /> */}
          <form onSubmit={handleSubmit} className='signin__form' noValidate>
            <TextField
              name='username'
              label={getLabelText()}
              fill
              required
              disabled={isFetching}
              autoFocus
              autoComplete='username'
            />
            <PasswordField
              name='password'
              label={i18n('field_password_label')}
              fill
              required
              disabled={isFetching}
              autoComplete='current-password'
            />
            <Checkbox
              label={i18n('signin_stay_signed_in')}
              name='remember_credentials'
              className='global-gateway-modal__checkbox'
              theme={THEMES.LIGHT}
              disabled={isFetching}
            />
            {onCancel ? (
              <div className='signin__button-row'>
                <Button className='signin__button' theme={THEMES.DARK} ghosted loading={isFetching} onClick={onCancel}>
                  {i18n('common_cancel')}
                </Button>
                <Button className='signin__button' type='submit' loading={isFetching}>
                  {i18n('signin_submit')}
                </Button>
              </div>
            ) : (
              <Button className='signin__button' type='submit' loading={isFetching}>
                {i18n('signin_submit')}
              </Button>
            )}
          </form>
          {showUnSuccessfulMessage && (
            <p>
              {i18n(
                'signin_forgot_username_or_password',
                [
                  <Anchor
                    className='signin__link'
                    key={0}
                    href={config.getRedirectUrl(
                      isShowMemberNumber
                        ? WINDOW_OBJECT_KEYS.FORGOT_MEMBERNUMBER_PAGE
                        : WINDOW_OBJECT_KEYS.FORGOT_USERNAME_PAGE
                    )}
                  >
                    {getForgotLinkText()}
                  </Anchor>,
                  <Anchor
                    className='signin__link'
                    key={1}
                    href={config.getRedirectUrl(WINDOW_OBJECT_KEYS.FORGOT_PASSWORD_PAGE)}
                  >
                    {i18n('signin_forgot_password_text')}
                  </Anchor>,
                ],
                { jsx: true }
              )}
            </p>
          )}
        </div>
      )}
    </Form>
  );
};

SignIn.defaultProps = {
  onCancel: null,
};

SignIn.propTypes = {
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  unAuthenticatedFlyout: PropTypes.bool,
};

export default SignIn;
